import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, NavLink } from 'react-router-dom';
import { getUserStats, getAdminDashboard } from '../components/DbServices';
import { getStoredUser } from '../utilities/encryption';

export default function Navbar() {
  const { logout } = useContext(AuthContext); // Injected from AuthContext
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const session = getStoredUser();
  const user = session?.data;
  const userRole = user?.role || 'user';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const fetchNavbarStats = async () => {
      try {
        if (userRole === 'admin') {
          const res = await getAdminDashboard();
          if (res?.status === 200) setStats(res.data);
        } else {
          const res = await getUserStats(user.id);
          
          if (res?.status === 200) setStats(res.data);
        }
      } catch (err) {
        console.error("Navbar Sync Error", err);
      }
    };
    fetchNavbarStats();
  }, [user.id, userRole]);

  // Helper: Real-time calculation for subscription expiry
  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return 0;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Real value calculation for progress bar
  const messageLimit = parseInt(stats?.message_limit) || 0;
  const messagesSent = parseInt(stats?.messages_sent) || 0;
  const messagePercentage = messageLimit > 0 ? Math.min((messagesSent / messageLimit) * 100, 100) : 0;
  const daysLeft = getDaysRemaining(stats?.expires_at);

  return (
    <nav className="navbar navbar-expand bg-white border-bottom px-4 sticky-top shadow-sm" style={{ height: '75px', zIndex: 1040 }}>
      <div className="container-fluid">

        {/* LEFT SECTION: DYNAMIC SYSTEM STATS */}
        <div className="d-flex align-items-center gap-4">
          {userRole === 'admin' ? (
            /* ADMIN NAV STATS - Colorful Gradients */
            <>
              <div className="d-none d-lg-block">
                <small className="text-muted d-block fw-black" style={{ fontSize: '0.6rem', letterSpacing: '0.5px' }}>GLOBAL REVENUE</small>
                <span className="fw-black text-gradient" style={{ background: 'linear-gradient(90deg, #10B981, #059669)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  ₹{stats?.summary?.finance?.total_revenue?.toLocaleString() || '0.00'}
                </span>
              </div>
              <div className="d-none d-lg-block border-start ps-4">
                <small className="text-muted d-block fw-black" style={{ fontSize: '0.6rem', letterSpacing: '0.5px' }}>NODE CONNECTIVITY</small>
                <span className="fw-bold text-primary d-flex align-items-center">
                  <span className="pulse-dot bg-primary me-2"></span>
                  {stats?.summary?.devices?.online_count || 0} Instances Online
                </span>
              </div>
            </>
          ) : (
            /* USER NAV STATS - Dynamic Values */
            <>




              <div className="d-none d-lg-block" style={{ width: '180px' }}>
                <div className="d-flex justify-content-between mb-1">
                  <small className="fw-black text-uppercase" style={{ fontSize: '0.6rem' }}>Quota: {messagesSent}/{messageLimit}</small>
                  <small className="fw-bold text-primary" style={{ fontSize: '0.6rem' }}>{Math.round(messagePercentage)}%</small>
                </div>
                <div className="progress rounded-pill" style={{ height: '6px', backgroundColor: '#f1f5f9' }}>
                  <div
                    className={`progress-bar rounded-pill ${messagePercentage > 80 ? 'bg-danger' : 'bg-primary'}`}
                    style={{ width: `${messagePercentage}%`, transition: 'width 1s ease' }}
                  ></div>
                </div>
              </div>

              <div className="d-none d-md-block border-start ps-4">
                <small className="text-muted d-block fw-black" style={{ fontSize: '0.6rem' }}>RENEWAL</small>
                <span className={`fw-bold small ${daysLeft <= 5 ? 'text-danger' : 'text-dark'}`}>
                  <i className={`fas fa-hourglass-half me-1 ${daysLeft <= 5 ? 'fa-spin' : ''}`}></i>
                  {daysLeft} Days Remaining
                </span>
              </div>

              <div className="d-none d-md-block border-start ps-4">
                <small className="text-muted d-block fw-black" style={{ fontSize: '0.6rem' }}>INSTANCES</small>
                <span className="fw-bold small text-indigo">
                  {stats?.devices_used || 0} / {stats?.device_limit || 0} Active
                </span>
              </div>
            </>
          )}
        </div>

        {/* RIGHT SECTION: USER PROFILE & ACTIONS */}
        <div className="ms-auto d-flex align-items-center gap-2">


          <div className="d-none d-xl-block me-3">
            <div className="position-relative overflow-hidden px-3 py-1 rounded-pill d-flex align-items-center gap-2 shadow-sm border-0 transition-all hover-lift"
              style={{
                background: stats?.plan_name?.toLowerCase() !== 'free'
                  ? 'linear-gradient(135deg, #2D0B5A 0%, #4C1D95 100%)' // Dark Deep Purple
                  : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)', // Dark Slate/Navy
                boxShadow: '0 4px 15px -3px rgba(0,0,0,0.3)',
                cursor: 'default',
                border: '1px solid rgba(255,255,255,0.1)' // Subtle outer glow
              }}>

              {/* Animated Shimmer Effect */}
              <div className="shimmer-effect"></div>

              <div className="d-flex align-items-center justify-content-center bg-white rounded-circle shadow-sm"
                style={{ width: '20px', height: '20px' }}>
                <i className={`fas ${stats?.plan_name?.toLowerCase() !== 'free' ? 'fa-crown text-warning' : 'fa-seedling text-success'}`}
                  style={{ fontSize: '0.6rem' }}></i>
              </div>

              <div className="d-flex flex-column">
                <small className="fw-black text-white" style={{ fontSize: '0.65rem', letterSpacing: '0.6px', lineHeight: '1' }}>
                  {stats?.plan_name?.toUpperCase() || 'FREE'}
                </small>
              </div>
            </div>

          </div>

          <div className="d-none d-sm-flex flex-column text-end me-2">
            <span className="fw-black text-dark small lh-1 mb-1">{user?.name}</span>
            <span className={`badge rounded-pill fw-black ${userRole === 'admin' ? 'bg-danger-subtle text-danger' : 'bg-primary-subtle text-primary'}`} style={{ fontSize: '0.55rem', width: 'fit-content', alignSelf: 'flex-end' }}>
              {userRole.toUpperCase()}
            </span>
          </div>

          <div className="dropdown">
            <button className="btn p-0 border-0 transition-all hover-scale" type="button" data-bs-toggle="dropdown">
              <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-black shadow-sm position-relative"
                style={{
                  width: '45px',
                  height: '45px',
                  background: userRole === 'admin' ? 'linear-gradient(135deg, #1e293b, #334155)' : 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  fontSize: '1.2rem'
                }}>
                {user?.name?.charAt(0).toUpperCase()}
                <span className="position-absolute bottom-0 end-0 bg-success border border-white border-2 rounded-circle" style={{ width: '12px', height: '12px' }}></span>
              </div>
            </button>

            <ul className="dropdown-menu dropdown-menu-end shadow-lg border-0 mt-3 p-2 rounded-4" style={{ minWidth: '240px' }}>
              <li className="p-3 bg-light rounded-4 mb-2">
                <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '10px' }}>Current Session</small>
                <div className="fw-bold text-dark truncate small">{user?.email}</div>
              </li>
              <li><NavLink className="dropdown-item rounded-3 py-2" to="/settings"><i className="fas fa-fingerprint me-2 text-primary"></i> Account Settings</NavLink></li>
              {userRole === 'user' && (
                <li><NavLink className="dropdown-item rounded-3 py-2 text-primary fw-bold" to="/pricing"><i className="fas fa-crown me-2"></i> Upgrade Plan</NavLink></li>
              )}
              <li><hr className="dropdown-divider opacity-50" /></li>
              <li>
                <button className="dropdown-item rounded-3 py-2 text-danger fw-black" onClick={handleLogout}>
                  <i className="fas fa-power-off me-2"></i> SIGN OUT
                </button>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </nav>
  );
}